import * as React from 'react';
import { useReducedMotion } from 'framer-motion';
import { cn } from '@/lib/utils';

export interface HandwritingRevealProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Total animation length in seconds (across the whole word). */
  duration?: number;
  /** Delay before the first letter lands (seconds). */
  delay?: number;
  /** Optional override for per-letter stagger (seconds). Falls back to a sensible
   *  derivation from `duration` and the word length. */
  letterStagger?: number;
  children: React.ReactNode;
}

/**
 * HandwritingReveal — letter-by-letter staggered drop-in. Each character lands
 * with a small overshoot (rotate + translate + scale → settled), which reads
 * as handwriting because the brain perceives the sequential stroke landing
 * pattern, not a uniform reveal.
 *
 * Why not clip-path?
 *   A left-to-right wipe reads as a curtain reveal rather than writing. It also
 *   clips the rotated bbox at the right edge for tilted Caveat (visible cut-off
 *   on the trailing glyph). Per-letter animation has no clipping issues and
 *   feels measurably more like real writing.
 *
 * Implementation notes:
 *   - Children must be a string. Non-string children render statically.
 *   - Whitespace preserved by swapping spaces for non-breaking spaces.
 *   - Plain React state + CSS transitions (no Framer Motion) for reliability.
 *   - Honors `prefers-reduced-motion`: text renders fully visible without
 *     animation.
 *
 * For literal per-stroke handwriting (e.g. a one-off marquee word), build a
 * separate <HandwritingPathReveal /> primitive that animates SVG paths via
 * stroke-dasharray. That requires hand-traced paths per word.
 */
export const HandwritingReveal = React.forwardRef<HTMLSpanElement, HandwritingRevealProps>(
  (
    {
      duration = 1.4,
      delay = 0.1,
      letterStagger,
      className,
      children,
      style,
      ...rest
    },
    ref,
  ) => {
    const prefersReducedMotion = useReducedMotion();
    const [revealed, setRevealed] = React.useState(false);

    // Trigger after mount; rAF ensures the browser commits the initial frame
    // before transitioning so each letter actually animates.
    React.useEffect(() => {
      if (prefersReducedMotion) return;
      let rafId = 0;
      const handle = window.setTimeout(() => {
        rafId = window.requestAnimationFrame(() => setRevealed(true));
      }, Math.max(0, delay * 1000));
      return () => {
        window.clearTimeout(handle);
        if (rafId) window.cancelAnimationFrame(rafId);
      };
    }, [delay, prefersReducedMotion]);

    const text = typeof children === 'string' ? children : null;

    // Reduced motion or non-string children: render fully visible.
    if (prefersReducedMotion || !text) {
      return (
        <span ref={ref} className={cn('inline-block', className)} style={style} {...rest}>
          {children}
        </span>
      );
    }

    const letters = Array.from(text);
    const stagger =
      typeof letterStagger === 'number'
        ? letterStagger
        : Math.min(0.16, Math.max(0.04, (duration * 0.5) / Math.max(1, letters.length)));
    const perLetterDuration = Math.max(0.4, duration * 0.55);

    return (
      <span ref={ref} className={cn('inline-block', className)} style={style} {...rest}>
        {letters.map((char, i) => {
          const letterDelay = i * stagger;
          // Pseudo-random per-letter jiggle so the drop doesn't feel mechanical.
          // Uses the letter index modulo to vary direction without true randomness
          // (deterministic + SSR-safe).
          const variant = i % 4;
          const initialRotate =
            variant === 0 ? -10 : variant === 1 ? -6 : variant === 2 ? -8 : -4;
          const initialY = variant === 0 || variant === 2 ? 10 : 14;

          return (
            <span
              key={`${char}-${i}`}
              aria-hidden={i > 0 ? true : undefined}
              style={{
                display: 'inline-block',
                whiteSpace: 'pre',
                transformOrigin: 'left bottom',
                transform: revealed
                  ? 'translate3d(0, 0, 0) rotate(0deg) scale(1)'
                  : `translate3d(0, ${initialY}px, 0) rotate(${initialRotate}deg) scale(0.78)`,
                opacity: revealed ? 1 : 0,
                transition: `transform ${perLetterDuration}s cubic-bezier(0.22, 1.5, 0.36, 1) ${letterDelay}s, opacity ${Math.min(perLetterDuration, 0.45)}s ease-out ${letterDelay}s`,
                willChange: 'transform, opacity',
              }}
            >
              {char === ' ' ? ' ' : char}
            </span>
          );
        })}
        {/* Screen-reader-friendly: announce the full word once, not letter-by-letter */}
        <span className="sr-only">{text}</span>
      </span>
    );
  },
);
HandwritingReveal.displayName = 'HandwritingReveal';
